/**
 * URL utilities for iframe routing and browser ↔ iframe URL synchronization.
 *
 * Flow: Browser URL → getRelativePath() → addRelativePath() → Iframe URL
 * Reverse: Iframe navigates → URL_SYNC message → useUrlSyncListener → Browser URL
 */

import { matchPath } from "react-router-dom";
import type { NavigationLink } from "../types";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export const removeTrailingSlash = (path: string): string =>
  path.endsWith("/") ? path.slice(0, -1) : path;

/**
 * Extracts the pathname prefix from an iframe URL.
 * e.g., "https://de.com/api/?token=X" → "/api"
 */
export const getIframePathPrefix = (iframeUrl: string | undefined): string => {
  if (!iframeUrl) return "";
  try {
    const urlObj = new URL(iframeUrl);
    return removeTrailingSlash(urlObj.pathname);
  } catch {
    return "";
  }
};

/**
 * Extracts the origin from an absolute URL, for comparing against a
 * MessageEvent.origin. e.g. "https://host.example/iam" → "https://host.example"
 */
export const getOrigin = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
};

export const getFromParams = (key: string): string | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const param = searchParams.get(key);
  return param;
};

/**
 * Strips pathPrefix from a URL to get the relative path for iframe navigation.
 * e.g., getRelativePath("/v2/data/api/clients", "/v2/data") → "/api/clients"
 */
export const getRelativePath = (url: string, pathPrefix?: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    if (!pathPrefix) return pathname;

    const match = matchPath(pathPrefix + "/*", pathname);
    return match ? pathname.slice(match.pathnameBase.length) : pathname;
  } catch {
    // If URL parsing fails (e.g. relative URL provided), try to remove pathPrefix
    return pathPrefix ? url.replace(pathPrefix, "") : url;
  }
};

/**
 * Appends a relative path to an iframe base URL.
 * The path should be a true relative suffix (e.g., "/clients", not "/api/clients").
 */
export const addRelativePath = (
  iframeUrl: string | undefined,
  path: string | undefined,
) => {
  if (!iframeUrl) return undefined;

  try {
    const urlObj = new URL(iframeUrl);
    const urlPath = urlObj.pathname.endsWith("/")
      ? urlObj.pathname.slice(0, -1)
      : urlObj.pathname;
    const cleanPath = path?.startsWith("/") ? path : `/${path}`;

    const newUrl = new URL(urlObj);
    if (cleanPath) {
      // Dedup: if cleanPath already starts with urlPath, use it directly to avoid doubling.
      // e.g., urlPath="/rex", cleanPath="/rex/conversations/123" → "/rex/conversations/123" not "/rex/rex/conversations/123"
      const newPathname =
        cleanPath.startsWith(urlPath + "/") || cleanPath === urlPath
          ? cleanPath
          : urlPath + cleanPath;
      newUrl.pathname = newPathname;
    }

    return newUrl.toString();
  } catch {
    // Fallback for invalid URLs
    return iframeUrl;
  }
};

export const addQueryParams = (
  url: string | undefined,
  searchParams: URLSearchParams,
) => {
  if (!url) return undefined;

  const urlObj = new URL(url);
  const currentSearch = new URLSearchParams(urlObj.search);
  searchParams.forEach((value, key) => {
    currentSearch.set(key, value);
  });
  urlObj.search = currentSearch.toString();
  return urlObj.toString();
};

/**
 * Route configuration for iframe pages. Generated from NavigationLink data.
 *
 * Key concept - basePath:
 * - basePath: The browser-side anchor for URL transformation (extraction/sync).
 *   Usually matches the route's own path (link.to), but may be the parent prefix
 *   when the iframe app's path prefix overlaps with the tail of link.to.
 *
 *   Example: link.to="/v2/data/api", iframe URL prefix="/api"
 *   → link.to ends with "/api", so basePath="/v2/data" (strip the overlap).
 *   URL sync: iframe reports "/api/clients", basePath "/v2/data" → "/v2/data/api/clients" ✓
 *   Browser→iframe: user at "/v2/data/api/clients", relative="/api/clients", dedup in addRelativePath ✓
 */
export interface RouteConfig {
  path: string;
  key: string;
  /** Browser-side base path for URL extraction/sync. May differ from the route path. */
  basePath?: string;
  activeLink?: {
    to: string;
    icon: IconDefinition;
    fullTitle: string;
  };
  iframe?: {
    id: string;
    url: string;
    key: string;
  };
  redirectTo?: string;
}

/**
 * Converts NavigationLink[] into RouteConfig[] for React Router.
 *
 * basePath is pre-adjusted to avoid path doubling during URL sync: when the iframe
 * app's path prefix mirrors the tail of link.to (e.g. link.to="/v2/data/api", prefix="/api"),
 * basePath is set to the parent path ("/v2/data") so that simple basePath+iframePath gives
 * the correct browser URL ("/v2/data/api/clients").
 */
export const getSublinkRoutes = (links: NavigationLink[]): RouteConfig[] => {
  const routes: RouteConfig[] = [];

  links.forEach((link) => {
    // Top-level link with iframe (no sublinks)
    if (link.iFrameUrl && (!link.subLinks || link.subLinks.length === 0)) {
      const prefix = getIframePathPrefix(link.iFrameUrl);
      const basePath =
        prefix && link.to.endsWith(prefix) ? link.to.slice(0, -prefix.length) : link.to;
      routes.push({
        path: `${link.to}/*`,
        key: link.to,
        basePath,
        activeLink: {
          to: link.to,
          icon: link.icon,
          fullTitle: link.fullTitle,
        },
        iframe: {
          id: link.title,
          url: link.iFrameUrl,
          key: link.to,
        },
      });
    } else if (link.subLinks?.[0]) {
      // Parent with sublinks - redirect to first sublink
      routes.push({
        path: link.to,
        key: `${link.to}-redirect`,
        redirectTo: link.subLinks[0].to,
      });
    }

    // Sublinks - basePath is the parent's path for correct URL mapping
    link.subLinks?.forEach((subLink) => {
      routes.push({
        path: `${subLink.to}/*`,
        key: subLink.to,
        basePath: link.to,
        activeLink: {
          to: subLink.to,
          icon: link.icon,
          fullTitle: subLink.title,
        },
        iframe: {
          id: subLink.title,
          url: subLink.iFrameUrl,
          key: subLink.to,
        },
      });
    });
  });

  return routes;
};
